import cx from "classnames";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { InvoiceItem } from "types/Models/invoice";
import { currency } from "utils/Currency";

type InvoicePreviewItemsProps = {
  items: InvoiceItem[];
  currencyCode?: string;
  hasItemLevelTax?: boolean;
};

const HEADER_CELL_CLASSES =
  "t-px-2.5 sm:t-px-1 t-py-1 t-bg-text-100 t-text-white t-font-normal";

const BODY_CELL_CLASSES = "t-px-2.5 sm:t-px-1 t-py-1.5 t-align-top";

const formatBillingPeriod = (item: InvoiceItem) => {
  if (
    !item.billing_period_enabled ||
    !item.billing_period_start_date ||
    !item.billing_period_end_date
  ) {
    return null;
  }

  return `${dayjs(item.billing_period_start_date).format(
    DISPLAY_DATE
  )} - ${dayjs(item.billing_period_end_date).format(DISPLAY_DATE)}`;
};

export const InvoicePreviewItems = ({
  items,
  currencyCode,
  hasItemLevelTax,
}: InvoicePreviewItemsProps) => {
  const formatAmount = (amount?: string | number | null) =>
    currency({ amount: amount || 0, currencyCode });

  return (
    <div className="t-border-0 t-border-b t-border-solid t-border-neutral-10 sm:t-overflow-x-auto">
      <table className="t-w-full t-border-separate t-border-spacing-0">
        <thead>
          <tr>
            <th
              className={cx(
                HEADER_CELL_CLASSES,
                "t-w-1/2 sm:t-w-auto t-text-left t-rounded-l-sm"
              )}
            >
              Item
            </th>
            <th className={cx(HEADER_CELL_CLASSES, "t-text-center")}>
              Quantity
            </th>
            <th className={cx(HEADER_CELL_CLASSES, "t-text-right")}>Rate</th>
            {hasItemLevelTax && (
              <th className={cx(HEADER_CELL_CLASSES, "t-text-center")}>Tax</th>
            )}
            <th
              className={cx(HEADER_CELL_CLASSES, "t-text-right t-rounded-r-sm")}
            >
              Amount
            </th>
          </tr>
        </thead>
        <tbody className="t-text-text-60 [&>tr:first-child>td]:t-pt-2">
          {items.map((item, index) => {
            const billingPeriod = formatBillingPeriod(item);
            const taxRate = item.tax_rate?.rate
              ? `${item.tax_rate.rate}%`
              : "-";

            return (
              <tr key={item.uuid || index}>
                <td className={BODY_CELL_CLASSES}>
                  {item.description}
                  {billingPeriod && (
                    <span className="t-block t-text-text-30">
                      {billingPeriod}
                    </span>
                  )}
                </td>
                <td className={cx(BODY_CELL_CLASSES, "t-text-center")}>
                  {item.quantity}
                </td>
                <td className={cx(BODY_CELL_CLASSES, "t-text-right")}>
                  {formatAmount(item.rate)}
                </td>
                {hasItemLevelTax && (
                  <td className={cx(BODY_CELL_CLASSES, "t-text-center")}>
                    {taxRate}
                  </td>
                )}
                <td className={cx(BODY_CELL_CLASSES, "t-text-right")}>
                  {formatAmount(item.amount)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
